import React, { useEffect, useState } from "react";
import FormField from "@cloudscape-design/components/form-field";
import Select from "@cloudscape-design/components/select";
import Button from "@cloudscape-design/components/button";
import Input from "@cloudscape-design/components/input";
import Textarea from "@cloudscape-design/components/textarea";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Modal from "@cloudscape-design/components/modal";
import Box from "@cloudscape-design/components/box";
import TokenGroup from "@cloudscape-design/components/token-group";
import Grid from "@cloudscape-design/components/grid";
import ColumnLayout from "@cloudscape-design/components/column-layout";
import { getCurrentUser, fetchAuthSession } from "aws-amplify/auth";

/**
 * Get a readable display name for the current user.
 * Priority: Full Name > Email > Username
 */
const getUserDisplayName = async () => {
  try {
    const user = await getCurrentUser();
    const session = await fetchAuthSession();
    
    if (session.tokens) {
      const payload = session.tokens.idToken.payload;
      
      // Try to get full name (given_name + family_name)
      if (payload.given_name && payload.family_name) {
        return `${payload.given_name} ${payload.family_name}`;
      }
      
      // Try to get just given_name
      if (payload.given_name) {
        return payload.given_name;
      }
      
      // Try to get email
      if (payload.email) {
        return payload.email;
      }
    }
    
    // Fallback to username (for non-SSO users, this will be readable)
    return user.username || user.userId || "Unknown User";
  } catch (error) {
    console.error("Error getting user display name:", error);
    return "Unknown User";
  }
};

export const ModalComponent = ({
  headers,
  data,
  index,
  updateData,
  visible,
  setVisible,
  action,
  type,
  hasColumn = false,
  columnConfig = null, // { left: ['field1', 'field2'], right: ['field3', 'field4'] }
}) => {
  // Helper function to initialize form data based on type
  const getInitialFormData = (inputData) => {
    const baseData = { ...inputData };
    
    // Only add threat-specific fields for threats
    if (type === "threats") {
      return {
        ...baseData,
        status: inputData.status || "To do",
        comments: inputData.comments || "",
        comments_edited_by: inputData.comments_edited_by || null,
        comments_edited_at: inputData.comments_edited_at || null,
        mitigation_edited_by: inputData.mitigation_edited_by || null,
        mitigation_edited_at: inputData.mitigation_edited_at || null,
        last_edited_by: inputData.last_edited_by || null,
        last_edited_at: inputData.last_edited_at || null,
        mitigations: inputData.mitigations || [],
        prerequisites: inputData.prerequisites || [],
      };
    }
    
    return baseData;
  };

  const [formData, setFormData] = React.useState(getInitialFormData(data));
  const [tempFormData, setTempFormData] = React.useState(getInitialFormData(data));
  const [newMitigation, setNewMitigation] = React.useState("");
  const [newPrerequisite, setNewPrerequisite] = React.useState("");
  const [isFormValid, setIsFormValid] = React.useState(false);

  const EnumOptions = {
    type: [
      { label: "Asset", value: "Asset" },
      { label: "Entity", value: "Entity" },
    ],
    likelihood: [
      { label: "Very High", value: "Very High" },
      { label: "High", value: "High" },
      { label: "Medium", value: "Medium" },
      { label: "Low", value: "Low" },
      { label: "Very Low", value: "Very Low" },
    ],
    status: [
      { label: "Not applicable", value: "Not applicable" },
      { label: "To do", value: "To do" },
      { label: "Done", value: "Done" },
    ],
  };

  const handleInputChange = async (key, value) => {
    // Track likelihood changes
    if (key === "likelihood" && value !== tempFormData.likelihood) {
      try {
        const displayName = await getUserDisplayName();
        const timestamp = new Date().toISOString();
        
        setTempFormData((prev) => ({
          ...prev,
          [key]: value,
          last_edited_by: displayName,
          last_edited_at: timestamp,
        }));
      } catch (error) {
        console.error("Error getting current user:", error);
        setTempFormData((prev) => ({
          ...prev,
          [key]: value,
        }));
      }
    } 
    // Track comments changes
    else if (key === "comments" && value !== tempFormData.comments) {
      setTempFormData((prev) => ({
        ...prev,
        [key]: value,
      }));
      try {
        const displayName = await getUserDisplayName();
        const timestamp = new Date().toISOString();
        setTempFormData((prev) => ({
          ...prev,
          comments_edited_by: displayName,
          comments_edited_at: timestamp,
        }));
      } catch (error) {
        console.error("Error getting current user:", error);
      }
    } 
    else {
      setTempFormData((prev) => ({
        ...prev,
        [key]: value,
      }));
    }
  };

  const validateForm = (formData) => {
    // Special validation for threats only: if status is set, comments must be filled
    // Only apply this rule if the form actually has status and comments fields
    const hasStatusField = headers.some(h => h.toLowerCase() === "status");
    const hasCommentsField = headers.some(h => h.toLowerCase() === "comments");
    
    if (hasStatusField && hasCommentsField) {
      if (formData.status && formData.status !== "" && (!formData.comments || formData.comments.trim() === "")) {
        return false;
      }
    }
    
    return headers.every((header) => {
      const key = header.toLowerCase();
      if (key === "mitigations") {
        return formData.mitigations && formData.mitigations.length > 0;
      }
      if (key === "prerequisites") {
        return formData.prerequisites && formData.prerequisites.length > 0;
      }
      // Status and comments are optional fields
      if (key === "status" || key === "comments") {
        return true;
      }
      return formData[key] && formData[key].trim() !== "";
    });
  };

  useEffect(() => {
    setIsFormValid(validateForm(tempFormData));
  }, [tempFormData]);

  const handleSave = () => {
    let updatedData = { ...tempFormData };
    
    // Only include threat-specific fields for threats
    if (type === "threats") {
      updatedData = {
        ...tempFormData,
        mitigations: tempFormData.mitigations || [],
        prerequisites: tempFormData.prerequisites || [],
      };
    }
    
    setFormData(updatedData);
    if (action === "edit") {
      updateData(type, index, updatedData);
    }
    if (action === "add") {
      updateData(type, -1, updatedData);
    }
    setVisible(false);
  };

  const handleDismiss = () => {
    setTempFormData(formData);
    setVisible(false);
  };

  const handleAddMitigation = async () => {
    if (newMitigation.trim()) {
      try {
        const displayName = await getUserDisplayName();
        const timestamp = new Date().toISOString();
        
        const updatedMitigations = [...(tempFormData.mitigations || []), newMitigation];
        setTempFormData((prev) => ({
          ...prev,
          mitigations: updatedMitigations,
          mitigation_edited_by: displayName,
          mitigation_edited_at: timestamp,
        }));
        setNewMitigation("");
      } catch (error) {
        console.error("Error getting current user:", error);
        const updatedMitigations = [...(tempFormData.mitigations || []), newMitigation];
        setTempFormData((prev) => ({
          ...prev,
          mitigations: updatedMitigations,
        }));
        setNewMitigation("");
      }
    }
  };

  const handleRemoveMitigation = async (indexToRemove) => {
    try {
      const displayName = await getUserDisplayName();
      const timestamp = new Date().toISOString();
      
      const updatedMitigations = tempFormData.mitigations.filter((_, i) => i !== indexToRemove);
      setTempFormData((prev) => ({
        ...prev,
        mitigations: updatedMitigations,
        mitigation_edited_by: displayName,
        mitigation_edited_at: timestamp,
      }));
    } catch (error) {
      console.error("Error getting current user:", error);
      const updatedMitigations = tempFormData.mitigations.filter((_, i) => i !== indexToRemove);
      setTempFormData((prev) => ({
        ...prev,
        mitigations: updatedMitigations,
      }));
    }
  };

  const handleAddPrerequisite = () => {
    if (newPrerequisite.trim()) {
      const updatedPrerequisites = [...(tempFormData.prerequisites || []), newPrerequisite];
      setTempFormData((prev) => ({
        ...prev,
        prerequisites: updatedPrerequisites,
      }));
      setNewPrerequisite("");
    }
  };

  const handleRemovePrerequisite = (indexToRemove) => {
    const updatedPrerequisites = tempFormData.prerequisites.filter((_, i) => i !== indexToRemove);
    setTempFormData((prev) => ({
      ...prev,
      prerequisites: updatedPrerequisites,
    }));
  };

  useEffect(() => {
    setFormData(getInitialFormData(data));
    setTempFormData(getInitialFormData(data));
  }, [data]);

  const renderField = (header) => {
    const key = header.toLowerCase();
    const label = header
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    if (key === "mitigations") {
      const showMitigationEdited = tempFormData.mitigation_edited_by && tempFormData.mitigation_edited_at;
      
      return (
        <React.Fragment key={key}>
          <FormField label={label}>
            <SpaceBetween direction="vertical" size="xs">
              <Grid gridDefinition={[{ colspan: { default: 8 } }, { colspan: { default: 4 } }]}>
                <Input
                  value={newMitigation}
                  onChange={({ detail }) => setNewMitigation(detail.value)}
                  placeholder="Type new mitigation"
                />
                <Button onClick={handleAddMitigation} disabled={!newMitigation.trim()}>
                  Add
                </Button>
              </Grid>
              <TokenGroup
                items={(tempFormData.mitigations || []).map((item, index) => {
                  // Parse URLs and create clickable links
                  const markdownLinkRegex = /\[([^\]]+)\]\(([^\)]+)\)/g;
                  const markdownMatches = [...item.matchAll(markdownLinkRegex)];
                  
                  if (markdownMatches.length > 0) {
                    const parts = [];
                    let lastIndex = 0;
                    
                    markdownMatches.forEach((match) => {
                      const [fullMatch, text, url] = match;
                      const matchIndex = match.index;
                      
                      if (matchIndex > lastIndex) {
                        parts.push(item.substring(lastIndex, matchIndex));
                      }
                      
                      parts.push(
                        <a 
                          key={`link-${index}-${matchIndex}`}
                          href={url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{ 
                            color: '#0073bb', 
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            pointerEvents: 'auto'
                          }}
                        >
                          {text}
                        </a>
                      );
                      
                      lastIndex = matchIndex + fullMatch.length;
                    });
                    
                    if (lastIndex < item.length) {
                      parts.push(item.substring(lastIndex));
                    }
                    
                    return { label: <>{parts}</>, dismissLabel: `Remove ${item}`, disabled: false };
                  }
                  
                  // Check for plain URLs in (Source: https://...) or (⚠️ External: description https://...) format
                  // Updated regex to handle optional text between prefix and URL
                  const plainUrlRegex = /\((Source|⚠️\s*External):\s*([^)]*?)(https?:\/\/[^\s)]+)\)/g;
                  const plainMatches = [...item.matchAll(plainUrlRegex)];
                  
                  if (plainMatches.length > 0) {
                    const parts = [];
                    let lastIndex = 0;
                    
                    plainMatches.forEach((match) => {
                      const [fullMatch, prefix, description, url] = match;
                      const matchIndex = match.index;
                      
                      if (matchIndex > lastIndex) {
                        parts.push(item.substring(lastIndex, matchIndex));
                      }
                      
                      parts.push(
                        <span key={`source-${index}-${matchIndex}`}>
                          ({prefix}: {description}<a 
                            href={url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            style={{ 
                              color: '#0073bb', 
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              pointerEvents: 'auto'
                            }}
                          >
                            {url}
                          </a>)
                        </span>
                      );
                      
                      lastIndex = matchIndex + fullMatch.length;
                    });
                    
                    if (lastIndex < item.length) {
                      parts.push(item.substring(lastIndex));
                    }
                    
                    return { label: <>{parts}</>, dismissLabel: `Remove ${item}`, disabled: false };
                  }
                  
                  // No links - return plain text
                  return { label: item, dismissLabel: `Remove ${item}`, disabled: false };
                })}
                onDismiss={({ detail }) => {
                  handleRemoveMitigation(detail.itemIndex);
                }}
              />
            </SpaceBetween>
          </FormField>
          {showMitigationEdited && (
            <Box color="text-status-info" fontSize="body-s" padding={{ bottom: "s" }}>
              <strong>Last edited by:</strong> {tempFormData.mitigation_edited_by} on {new Date(tempFormData.mitigation_edited_at).toLocaleString()}
            </Box>
          )}
        </React.Fragment>
      );
    }

    if (key === "prerequisites") {
      return (
        <FormField key={key} label={label}>
          <SpaceBetween direction="vertical" size="xs">
            <Grid gridDefinition={[{ colspan: { default: 8 } }, { colspan: { default: 4 } }]}>
              <Input
                value={newPrerequisite}
                onChange={({ detail }) => setNewPrerequisite(detail.value)}
                placeholder="Type new prerequisite"
              />
              <Button onClick={handleAddPrerequisite} disabled={!newPrerequisite.trim()}>
                Add
              </Button>
            </Grid>
            <TokenGroup
              items={(tempFormData.prerequisites || []).map((item, index) => ({
                label: item,
                dismissLabel: `Remove ${item}`,
                disabled: false,
              }))}
              onDismiss={({ detail }) => {
                handleRemovePrerequisite(detail.itemIndex);
              }}
            />
          </SpaceBetween>
        </FormField>
      );
    }

    // Special handling for description field - use Textarea
    if (key === "description") {
      return (
        <FormField key={key} label={label}>
          <Textarea
            onChange={({ detail }) => handleInputChange(key, detail.value)}
            value={tempFormData[key] || ""}
            placeholder={`Enter ${label.toLowerCase()}`}
            rows={3}
          />
        </FormField>
      );
    }

    // Special handling for impact field - use Textarea with more rows
    if (key === "impact") {
      // Remove the first line if it's just the impact level (High/Medium/Low)
      let impactValue = tempFormData[key] || "";
      const lines = impactValue.split('\n');
      if (lines.length > 1 && lines[0].match(/^(High|Medium|Low)$/)) {
        impactValue = lines.slice(1).join('\n');
      }
      
      return (
        <FormField key={key} label={label}>
          <Textarea
            onChange={({ detail }) => handleInputChange(key, detail.value)}
            value={impactValue}
            placeholder="Enter impact details"
            rows={6}
          />
        </FormField>
      );
    }

    // Special handling for comments field - use Textarea
    if (key === "comments") {
      const statusSelected = tempFormData.status && tempFormData.status !== "";
      const showCommentsEdited = tempFormData.comments_edited_by && tempFormData.comments_edited_at;
      
      return (
        <React.Fragment key={key}>
          <FormField 
            label={label}
            description={statusSelected ? "Required when status is set" : "Optional notes about this threat"}
            errorText={statusSelected && (!tempFormData[key] || tempFormData[key].trim() === "") ? "Comments are mandatory when status is set" : ""}
          >
            <Textarea
              onChange={({ detail }) => handleInputChange(key, detail.value)}
              value={tempFormData[key] || ""}
              placeholder="Enter comments or notes"
              rows={3}
            />
          </FormField>
          {showCommentsEdited && (
            <Box color="text-status-info" fontSize="body-s" padding={{ bottom: "s" }}>
              <strong>Last edited by:</strong> {tempFormData.comments_edited_by} on {new Date(tempFormData.comments_edited_at).toLocaleString()}
            </Box>
          )}
        </React.Fragment>
      );
    }

    // Special handling for example field - use Textarea
    if (key === "example") {
      return (
        <FormField key={key} label={label}>
          <Textarea
            onChange={({ detail }) => handleInputChange(key, detail.value)}
            value={tempFormData[key] || ""}
            placeholder={`Enter ${label.toLowerCase()}`}
            rows={3}
          />
        </FormField>
      );
    }

    if (key in EnumOptions) {
      const isLikelihood = key === "likelihood";
      const showLastEdited = isLikelihood && tempFormData.last_edited_by && tempFormData.last_edited_at;
      
      return (
        <React.Fragment key={key}>
          <FormField 
            label={label}
            description={showLastEdited ? `Last edited by: ${tempFormData.last_edited_by} on ${new Date(tempFormData.last_edited_at).toLocaleString()}` : undefined}
          >
            <Select
              selectedOption={
                tempFormData[key]
                  ? EnumOptions[key].find((opt) => opt.value === tempFormData[key])
                  : null
              }
              onChange={({ detail }) => handleInputChange(key, detail.selectedOption.value)}
              options={EnumOptions[key]}
              placeholder={key === "status" ? "Select status" : `Select ${label.toLowerCase()}`}
            />
          </FormField>
          {showLastEdited && (
            <Box color="text-status-info" fontSize="body-s" padding={{ bottom: "s" }}>
              <strong>Last edited by:</strong> {tempFormData.last_edited_by} on {new Date(tempFormData.last_edited_at).toLocaleString()}
            </Box>
          )}
        </React.Fragment>
      );
    }

    return (
      <FormField key={key} label={label}>
        <Input
          onChange={({ detail }) => handleInputChange(key, detail.value)}
          value={tempFormData[key] || ""}
        />
      </FormField>
    );
  };

  const renderContent = () => {
    if (hasColumn && columnConfig) {
      // Custom field distribution - specify exactly which fields go where
      const leftFields = columnConfig.left
        .map((headerName) => headers.find((h) => h.toLowerCase() === headerName.toLowerCase()))
        .filter(Boolean) // Remove any undefined values if field name doesn't exist
        .map((header) => renderField(header));

      const rightFields = columnConfig.right
        .map((headerName) => headers.find((h) => h.toLowerCase() === headerName.toLowerCase()))
        .filter(Boolean) // Remove any undefined values if field name doesn't exist
        .map((header) => renderField(header));

      return (
        <ColumnLayout borders="vertical" columns={2}>
          <SpaceBetween size="l">{leftFields}</SpaceBetween>
          <SpaceBetween size="l">{rightFields}</SpaceBetween>
        </ColumnLayout>
      );
    }

    if (hasColumn) {
      // Default even split when hasColumn is true but no columnConfig provided
      const fields = headers.map((header) => renderField(header));
      const midpoint = Math.ceil(fields.length / 2);
      const firstColumnFields = fields.slice(0, midpoint);
      const secondColumnFields = fields.slice(midpoint);

      return (
        <ColumnLayout borders="vertical" columns={2}>
          <SpaceBetween size="l">{firstColumnFields}</SpaceBetween>
          <SpaceBetween size="l">{secondColumnFields}</SpaceBetween>
        </ColumnLayout>
      );
    }

    // Default single column layout
    return <SpaceBetween size="xxl">{headers.map((header) => renderField(header))}</SpaceBetween>;
  };

  return (
    <Modal
      onDismiss={handleDismiss}
      visible={visible}
      size={hasColumn ? "large" : "medium"}
      header={`${action === "edit" ? "Edit" : "Add"} item`}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={handleSave} variant="primary" disabled={!isFormValid}>
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      {renderContent()}
    </Modal>
  );
};
